<template>
  <v-card flat>
    <FuelExpenseFormHeader
      :gasTankStationId.sync="form.gasTankStationId"
      :invoiceNumber.sync="form.invoiceNumber"
      :invoiceDate.sync="form.invoiceDate"
      :gasStations="gasStations"
      :validateState="validateState('gasTankStationId')"
      :invoiceNumberValidateState="validateState('invoiceNumber')"
      :invoiceDateValidateState="validateState('invoiceDate')"
    />

    <div class="form-group mt-5">
      <label>
        <input type="checkbox" v-model="form.toAccounting" />
        {{ $t("LABELS.TO_ACCOUNTING") }}
      </label>
    </div>

    <FuelExpenseFormDefaultMachine
      :defaultMachine.sync="defaultMachine"
      :vehicles="vehicles"
    />

    <FuelExpenseFormTotals
      :totalNoVat="form.totalNoVat"
      :vat="form.vat"
      :totalWithVat="form.totalWithVat"
      :showTotals="form.rows.length > 0"
    />

    <FuelExpenseFormRowsList
      :rows="form.rows"
      :fuelTypes="fuelTypes"
      :vehicles="vehicles"
      :staffList="staffList"
      :rowsWithErrors="rowsWithErrors"
      :rowValidationErrors="rowValidationErrors"
      @add-row="addNewRow"
      @delete-row="deleteRow"
      @update-row="updateRowField"
      @add-tank-charge="addTankCharge"
      @delete-tank-charge="deleteTankCharge"
      @update-tank-charge="updateTankCharge"
    />
  </v-card>
</template>

<script>
import { FETCH_GAS_STATIONS } from "@/modules/gas-stations/store/gasstation.module";
import { FETCH_FUELS_TYPES } from "@/modules/fuels-types/store/fuel-type.module";
import { FETCH_MACHINE_AND_TANK_OPTIONS } from "@/modules/tank-charges/store/tankcharges.module";
import { FETCH_ALL_STAFF } from "@/modules/staff/store/staff.module";

import FuelExpenseFormHeader from "@/modules/fuel-expenses/components/FuelExpenseFormHeader.vue";
import FuelExpenseFormDefaultMachine from "@/modules/fuel-expenses/components/FuelExpenseFormDefaultMachine.vue";
import FuelExpenseFormTotals from "@/modules/fuel-expenses/components/FuelExpenseFormTotals.vue";
import FuelExpenseFormRowsList from "@/modules/fuel-expenses/components/FuelExpenseFormRowsList.vue";

import { validationMixin } from "vuelidate";
import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";

export default {
  name: "FuelExpensesForm",
  mixins: [validationMixin, formBuilderMixin],
  components: {
    FuelExpenseFormHeader,
    FuelExpenseFormDefaultMachine,
    FuelExpenseFormTotals,
    FuelExpenseFormRowsList
  },
  props: {
    form: Object
  },
  validations() {
    const tmpValidationObject = {
      form: {
        gasTankStationId: this.setItemValidations({
          required: true
        }),
        invoiceNumber: this.setItemValidations({
          required: true
        }),
        invoiceDate: this.setItemValidations({
          required: true,
          minLength: 2
        })
      }
    };

    return tmpValidationObject;
  },
  data() {
    return {
      gasStations: [],
      fuelTypes: [],
      vehicles: [],
      staffList: [],
      newRowCounter: 0,
      rowsWithErrors: [],
      rowValidationErrors: {},
      defaultMachine: null
    };
  },
  watch: {
    "form.rows": {
      handler() {
        this.recalculateTotals();
      },
      deep: true
    }
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    validateRows() {
      const errors = [];
      const validationErrors = {};

      this.form.rows.forEach((row, index) => {
        const rowErrors = {};

        if (!row.fuelTypeId) {
          rowErrors.fuelTypeId = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        }

        if (!row.tankCharges || row.tankCharges.length === 0) {
          rowErrors.tankCharges = true;
          if (!errors.includes(index)) {
            errors.push(index);
          }
        } else {
          const tankChargeErrors = [];
          row.tankCharges.forEach((charge, chargeIndex) => {
            const chargeErrors = {};

            if (
              !charge.fuelable ||
              !charge.fuelableId ||
              !charge.fuelableType ||
              !charge.fuelableName
            ) {
              chargeErrors.fuelable = true;
            }

            if (Object.keys(chargeErrors).length > 0) {
              tankChargeErrors[chargeIndex] = chargeErrors;
              if (!errors.includes(index)) {
                errors.push(index);
              }
            }
          });

          if (
            tankChargeErrors.length > 0 ||
            Object.keys(tankChargeErrors).length > 0
          ) {
            rowErrors.tankChargeErrors = tankChargeErrors;
          }
        }

        if (Object.keys(rowErrors).length > 0) {
          validationErrors[index] = rowErrors;
        }
      });

      this.$set(this, "rowsWithErrors", errors);
      this.$set(this, "rowValidationErrors", validationErrors);

      console.log("Validation - Rows with errors:", this.rowsWithErrors);
      console.log("Validation - Error details:", this.rowValidationErrors);

      return this.rowsWithErrors.length === 0;
    },
    hasRowError(rowIndex) {
      return this.rowsWithErrors.includes(rowIndex);
    },
    hasFieldError(rowIndex, fieldName) {
      return (
        this.rowValidationErrors[rowIndex] &&
        this.rowValidationErrors[rowIndex][fieldName]
      );
    },
    async loadInitialData() {
      await Promise.all([
        this.$store.dispatch(FETCH_GAS_STATIONS).then(data => {
          this.gasStations = data.data.items;
        }),
        this.$store.dispatch(FETCH_FUELS_TYPES).then(data => {
          this.fuelTypes = data.data.items;
        }),
        this.$store.dispatch(FETCH_MACHINE_AND_TANK_OPTIONS).then(data => {
          this.vehicles = data.data.map(v => ({
            id: v.id,
            name: v.name,
            type: v.type,
            isMileage: v.isMileage,
            lastUsedStaffId: v.lastUsedStaffId,
            staffName: v.staffName
          }));
        }),
        this.$store.dispatch(FETCH_ALL_STAFF, "?active=1").then(data => {
          this.staffList = data.data.items || [];
        })
      ]);
    },
    addNewRow() {
      if (!this.form.rows) {
        this.$set(this.form, "rows", []);
      }

      this.form.rows.push({
        id: null,
        tempId: `temp-${this.newRowCounter++}`,
        fuelType: "",
        fuelTypeId: null,
        totalQty: 0,
        price: 0,
        totalPrice: 0,
        tankCharges: []
      });
    },
    deleteRow(index) {
      const row = this.form.rows[index];

      if (row.id) {
        this.$emit("track-deleted-row", row.id);

        if (row.tankCharges && row.tankCharges.length > 0) {
          row.tankCharges.forEach(charge => {
            if (charge.id) {
              this.$emit("track-deleted-tank-charge", charge.id);
            }
          });
        }
      }

      this.form.rows.splice(index, 1);
      this.recalculateTotals();

      const newErrors = this.rowsWithErrors
        .filter(i => i !== index)
        .map(i => (i > index ? i - 1 : i));
      this.$set(this, "rowsWithErrors", newErrors);

      const newValidationErrors = {};
      Object.keys(this.rowValidationErrors).forEach(key => {
        const idx = parseInt(key);
        if (idx !== index) {
          const newIdx = idx > index ? idx - 1 : idx;
          newValidationErrors[newIdx] = this.rowValidationErrors[key];
        }
      });
      this.$set(this, "rowValidationErrors", newValidationErrors);
    },
    addTankCharge(rowIndex) {
      const row = this.form.rows[rowIndex];
      if (!row.tankCharges) {
        this.$set(row, "tankCharges", []);
      }

      const gasTankStationId = this.form.gasTankStationId || null;
      const gasTankStationName =
        this.gasStations.find(gs => gs.id === gasTankStationId)?.name || "";

      let fuelable = null;
      let fuelableId = null;
      let fuelableType = null;
      let fuelableName = "";
      let staffId = null;
      let staffName = "";
      let isMileage = true;

      if (this.defaultMachine) {
        fuelable = this.defaultMachine;
        fuelableId = this.defaultMachine.id;
        fuelableType = this.defaultMachine.type;
        fuelableName = this.defaultMachine.name;
        isMileage = this.defaultMachine.isMileage;

        if (
          this.defaultMachine.type === "OwnMachineExpense" ||
          this.defaultMachine.type === "StaffMachineExpense" ||
          this.defaultMachine.type === "RentedMachineExpense"
        ) {
          if (this.defaultMachine.lastUsedStaffId) {
            staffId = this.defaultMachine.lastUsedStaffId;
            const staff = this.staffList.find(
              s => s.id === this.defaultMachine.lastUsedStaffId
            );
            staffName = staff ? staff.name : "";
          }
        }
      }

      row.tankCharges.push({
        id: null,
        tempId: `temp-charge-${this.newRowCounter++}`,
        gasTankStationId: gasTankStationId,
        gasTankStationName: gasTankStationName,
        staffId: staffId,
        fuelable: fuelable,
        fuelableId: fuelableId,
        fuelableType: fuelableType,
        fuelableName: fuelableName,
        liters: 0,
        mileage: 0,
        isMileage: isMileage,
        expense: 0,
        dateCharged: new Date().toISOString().split("T")[0],
        fuelExpenseRowId: row.id,
        staffName: staffName,
        dateMenu: false
      });

      if (this.rowValidationErrors[rowIndex]?.tankCharges) {
        const newValidationErrors = { ...this.rowValidationErrors };
        delete newValidationErrors[rowIndex].tankCharges;
        if (Object.keys(newValidationErrors[rowIndex]).length === 0) {
          delete newValidationErrors[rowIndex];
          const newErrors = this.rowsWithErrors.filter(i => i !== rowIndex);
          this.$set(this, "rowsWithErrors", newErrors);
        }
        this.$set(this, "rowValidationErrors", newValidationErrors);
      }
    },
    deleteTankCharge(rowIndex, chargeIndex) {
      const row = this.form.rows[rowIndex];
      const charge = row.tankCharges[chargeIndex];

      if (charge.id) {
        this.$emit("track-deleted-tank-charge", charge.id);
      }

      row.tankCharges.splice(chargeIndex, 1);
      this.recalculateRowFromTankCharges(row);

      if (row.tankCharges.length === 0) {
        const newValidationErrors = { ...this.rowValidationErrors };
        if (!newValidationErrors[rowIndex]) {
          newValidationErrors[rowIndex] = {};
        }
        newValidationErrors[rowIndex].tankCharges = true;
        this.$set(this, "rowValidationErrors", newValidationErrors);

        if (!this.rowsWithErrors.includes(rowIndex)) {
          const newErrors = [...this.rowsWithErrors, rowIndex];
          this.$set(this, "rowsWithErrors", newErrors);
        }
      }
    },
    updateRowField(index, field, value) {
      const row = this.form.rows[index];

      if (field === "fuelType") {
        row.fuelType = value;
        const fuelType = this.fuelTypes.find(ft => ft.name === value);
        row.fuelTypeId = fuelType ? fuelType.id : null;

        if (row.fuelTypeId && this.rowValidationErrors[index]?.fuelTypeId) {
          const newValidationErrors = { ...this.rowValidationErrors };
          delete newValidationErrors[index].fuelTypeId;
          if (Object.keys(newValidationErrors[index]).length === 0) {
            delete newValidationErrors[index];
            const newErrors = this.rowsWithErrors.filter(i => i !== index);
            this.$set(this, "rowsWithErrors", newErrors);
          }
          this.$set(this, "rowValidationErrors", newValidationErrors);
        }
      } else {
        row[field] = value;
      }

      if (field === "totalQty" || field === "price") {
        this.updateTotalPrice(row);
        this.recalculateTotals();
      }
    },
    updateTankCharge(rowIndex, chargeIndex, field, value) {
      const charge = this.form.rows[rowIndex].tankCharges[chargeIndex];

      if (field === "fuelable" && value) {
        charge.fuelable = value;
        charge.fuelableId = value.id;
        charge.fuelableType = value.type;
        charge.fuelableName = value.name;
        charge.isMileage = value.isMileage;

        if (
          value.type === "OwnMachineExpense" ||
          value.type === "StaffMachineExpense" ||
          value.type === "RentedMachineExpense"
        ) {
          if (value.lastUsedStaffId) {
            charge.staffId = value.lastUsedStaffId;
            const staff = this.staffList.find(
              s => s.id === value.lastUsedStaffId
            );
            charge.staffName = staff ? staff.name : "";
          } else {
            charge.staffId = null;
            charge.staffName = "";
          }
        } else if (value.type === "TankExpense") {
          charge.staffId = null;
          charge.staffName = "";
        }

        if (
          this.rowValidationErrors[rowIndex]?.tankChargeErrors?.[chargeIndex]
            ?.fuelable
        ) {
          const newValidationErrors = { ...this.rowValidationErrors };
          if (newValidationErrors[rowIndex]?.tankChargeErrors?.[chargeIndex]) {
            delete newValidationErrors[rowIndex].tankChargeErrors[chargeIndex]
              .fuelable;

            if (
              Object.keys(
                newValidationErrors[rowIndex].tankChargeErrors[chargeIndex]
              ).length === 0
            ) {
              delete newValidationErrors[rowIndex].tankChargeErrors[
                chargeIndex
              ];
            }

            if (
              Object.keys(newValidationErrors[rowIndex].tankChargeErrors)
                .length === 0
            ) {
              delete newValidationErrors[rowIndex].tankChargeErrors;
            }

            if (Object.keys(newValidationErrors[rowIndex]).length === 0) {
              delete newValidationErrors[rowIndex];
              const newErrors = this.rowsWithErrors.filter(i => i !== rowIndex);
              this.$set(this, "rowsWithErrors", newErrors);
            }

            this.$set(this, "rowValidationErrors", newValidationErrors);
          }
        }
      } else if (field === "staffId") {
        charge.staffId = value;
        if (value === -1) {
          charge.staffName = charge.fuelable.staffName;
        } else {
          const staff = this.staffList.find(s => s.id === value);
          charge.staffName = staff ? staff.name : "";
        }
      } else {
        charge[field] = value;
      }

      if (field === "liters" || field === "expense") {
        if (typeof charge.expense === "object" && charge.expense.formatted) {
          charge.expense = parseFloat(
            charge.expense.formatted.replace(",", "")
          );
        }
        this.recalculateRowFromTankCharges(this.form.rows[rowIndex]);
      }
    },
    recalculateRowFromTankCharges(row) {
      if (!row.tankCharges || row.tankCharges.length === 0) {
        return;
      }

      const totalLiters = row.tankCharges.reduce(
        (sum, charge) => sum + (parseFloat(charge.liters) || 0),
        0
      );

      row.totalQty = totalLiters.toFixed(2);

      if (row.price && parseFloat(row.price) > 0) {
        this.updateTotalPrice(row);
      }
    },
    updateTotalPrice(row) {
      const quantity = parseFloat(row.totalQty) || 0;
      const price = parseFloat(row.price) || 0;
      row.totalPrice = (quantity * price).toFixed(2);
    },
    recalculateTotals() {
      const totalSum = this.form.rows
        .reduce((sum, r) => sum + parseFloat(r.totalPrice || 0), 0)
        .toFixed(2);

      this.form.totalNoVat = totalSum;
      const vatAmount = (parseFloat(totalSum) * 0.2).toFixed(2);
      this.form.vat = vatAmount;
      this.form.totalWithVat = (
        parseFloat(totalSum) + parseFloat(vatAmount)
      ).toFixed(2);
    }
  }
};
</script>

<style scoped>
.input-lg input {
  font-size: 14px;
  font-weight: bold;
}
.vs__dropdown-toggle {
  background-color: #fff;
}

.is-invalid,
.is-invalid >>> .v-input__control,
.is-invalid >>> .v-select__selection,
.is-invalid >>> input,
.is-invalid >>> .vs__dropdown-toggle {
  border-color: #f64e60 !important;
  background-color: #ffe2e5 !important;
}

.is-invalid >>> .v-text-field__slot input {
  color: #f64e60;
}

#item-bol-list-group label {
  font-weight: bold;
  font-size: 15px;
}

.sort_div_holder {
  position: relative;
}
.sort_div {
  display: inline-block;
  padding: 16px 10px;
}
.sort_div_filter {
  position: absolute;
  top: 8px;
  right: -34px;
}

.v-list--dense .v-list-item,
.v-list-item--dense {
  min-height: 20px !important;
  height: 2rem;
}

.v-application--is-ltr .v-list-item__action:first-child,
.v-application--is-ltr .v-list-item__icon:first-child {
  margin-right: 0.5rem !important;
}

.v-list-item--link {
  transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.v-list-item--link:hover {
  background-color: rgba(0, 0, 0, 0.13);
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
  font-size: 13px !important;
}

.v-data-table > .v-data-table__wrapper > table > thead > th.sortable {
  position: relative;
}

.v-data-table-header__icon {
  height: 1px !important;
  float: right !important;
  top: -27px !important;
  right: -3px;
}
@media (min-width: 1264px) {
  .container {
    max-width: 100% !important;
  }
}

/* Rows Table Styles */
.rows-table {
  width: 100%;
  border-collapse: collapse;
}

.rows-table th,
.rows-table td {
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.fuel-type-header {
  width: 40%;
}

.fuel-type-cell {
  width: 40%;
}

.fuel-type-field {
  min-width: 200px;
  max-width: 100%;
}

/* Tank Charge Single Row Styles */
.tank-charge-row {
  background-color: #f8f9fa;
}

.tank-charge-cell {
  padding: 12px 16px !important;
}

.tank-charge-single {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  background-color: white;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.charge-field {
  display: flex;
  flex-direction: column;
  min-width: 120px;
  flex: 1;
}

.charge-field-wide {
  flex: 2;
}

.charge-label {
  color: #666;
  font-size: 11px;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 500;
}

.charge-input {
  margin-top: 0;
}

.charge-actions-field {
  flex: 0;
  min-width: auto;
}

.add-charge-row {
  background-color: #f8f9fa;
}

.add-charge-cell {
  padding: 8px 16px !important;
  text-align: center;
}

@media (max-width: 1200px) {
  .tank-charge-single {
    flex-wrap: wrap;
  }

  .charge-field {
    min-width: 150px;
  }

  .charge-field-wide {
    flex: 1 1 200px;
  }
}

@media (max-width: 768px) {
  .tank-charge-single {
    flex-direction: column;
    align-items: stretch;
  }

  .charge-field,
  .charge-field-wide {
    width: 100%;
    flex: 1;
  }
}
</style>
